import { useCallback } from 'react';

import { updateTab } from './workspaceTabUtils';

/**
 * Widget, notes and marker handlers, bound per tab.
 *
 * Every tab is mounted at once — only the active one is visible — so these are
 * factories rather than plain handlers: a hidden tab's grid still needs
 * callbacks that write to its own tab and not to whichever tab is on screen.
 */
export default function useTabWidgets({ layoutConfig, activeTab, updateLayoutAndPersist }) {
  const writeToTab = useCallback((tabId, patch) => {
    updateLayoutAndPersist(updateTab(layoutConfig, tabId, patch));
  }, [layoutConfig, updateLayoutAndPersist]);

  const tabById = useCallback(
    (tabId) => (layoutConfig.tabs || []).find((t) => t.id === tabId),
    [layoutConfig.tabs]
  );

  const handleAddWidget = useCallback((widgetConfig) => {
    if (!activeTab) return;
    writeToTab(activeTab.id, { widgets: [...activeTab.widgets, widgetConfig] });
  }, [activeTab, writeToTab]);

  const makeRemoveWidget = useCallback((tabId) => (widgetId) => {
    const tab = tabById(tabId);
    if (!tab) return;
    writeToTab(tabId, { widgets: tab.widgets.filter((w) => w.id !== widgetId) });
  }, [tabById, writeToTab]);

  const makeUpdateWidget = useCallback((tabId) => (widgetId, updates) => {
    const tab = tabById(tabId);
    if (!tab) return;
    writeToTab(tabId, { widgets: tab.widgets.map((w) => (w.id === widgetId ? { ...w, ...updates } : w)) });
  }, [tabById, writeToTab]);

  const makeLayoutChange = useCallback((tabId) => (updatedWidgets) => {
    writeToTab(tabId, { widgets: updatedWidgets });
  }, [writeToTab]);

  const makeUpdateTabNotes = useCallback((tabId) => (notes) => {
    writeToTab(tabId, { notes });
  }, [writeToTab]);

  const makeUpdateTabMarkers = useCallback((tabId) => (markers) => {
    writeToTab(tabId, { timelineMarkers: markers });
  }, [writeToTab]);

  return {
    handleAddWidget,
    makeRemoveWidget,
    makeUpdateWidget,
    makeLayoutChange,
    makeUpdateTabNotes,
    makeUpdateTabMarkers,
  };
}
